import {useState, useRef, useEffect, useCallback} from 'react'
import {useTheme} from '@/contexts/theme-context'
import {useMutation} from '@tanstack/react-query'
import Editor, {type Monaco} from '@monaco-editor/react'
import type {editor} from 'monaco-editor'
import {
    CheckCircle,
    Database,
    Loader2,
    Sparkles,
    X,
} from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {ScriptSqlDialog} from '@/components/shared/ScriptSqlDialog'
import {cn} from '@/lib/utils'
import {useToast} from '@/hooks/use-toast'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Textarea} from '@/components/ui/textarea'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {ErrorBanner} from '@/components/ui/error-banner'

/* eslint-disable i18next/no-literal-string -- technical IDs, config keys, Monaco options, and CSS classes */

// ============================================================================
// Constants
// ============================================================================

const ErrorCode = {
    LLM_NOT_CONFIGURED: 'LLM_NOT_CONFIGURED',
} as const

// ============================================================================
// Types
// ============================================================================

interface ValidationError {
    line: number
    column: number
    endColumn?: number
    message: string
}

interface ValidationResponse {
    valid: boolean
    errors: ValidationError[]
}

interface AssistResponse {
    suggestion: string
}

type SqlDialogState =
    | {open: false}
    | {open: true; initialSql: string | undefined; hasSelection: boolean}

const SQL_DIALOG_CLOSED: SqlDialogState = {open: false}

export interface ScriptEditorCoreProps {
    /** The current script content */
    script: string
    /** Called when the script content changes */
    onScriptChange: (script: string) => void
    /** Optional CSS class name for the outer container */
    className?: string
    /**
     * Suppress the built-in Check Syntax toolbar (button, success badge,
     * error summary bar, Fix with AI).
     *
     * Pass `true` when the parent dialog already exposes its own validation
     * action — e.g. the connector script config dialogs, whose "Validate
     * Script" button hits a connector-specific endpoint that covers the same
     * static parse plus supported-operation discovery. Avoids two near-
     * identical buttons side-by-side. The standalone ScriptEditorPage leaves
     * it unset so job scripts (which have no outer validator) keep the
     * button.
     */
    hideSyntaxToolbar?: boolean
    /**
     * Externally-produced compile errors to render as Monaco markers.
     *
     * When the parent owns validation (connector dialogs with `hideSyntaxToolbar`),
     * pass the validator's structured errors here so the editor can light up
     * inline squiggles without the parent having to poke at the Monaco instance.
     */
    externalSyntaxErrors?: ValidationError[] | null
}

// ============================================================================
// Main Component
// ============================================================================

export function ScriptEditorCore({
    script,
    onScriptChange,
    className,
    hideSyntaxToolbar = false,
    externalSyntaxErrors = null,
}: ScriptEditorCoreProps) {
    const i18n = useI18n()
    const {toast} = useToast()
    const {resolvedTheme} = useTheme()
    const services = useServices()

    // Editor refs
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
    const monacoRef = useRef<Monaco | null>(null)

    // Syntax check state
    const [syntaxErrors, setSyntaxErrors] = useState<ValidationError[]>()
    const [showAllErrors, setShowAllErrors] = useState(false)
    const [syntaxSuccess, setSyntaxSuccess] = useState(false)
    const [syntaxNetworkError, setSyntaxNetworkError] = useState<Error | null>(null)
    const [scriptModifiedAfterCheck, setScriptModifiedAfterCheck] = useState(false)
    const syntaxSuccessTimerRef = useRef<ReturnType<typeof setTimeout>>()

    // Assist panel state
    const [assistOpen, setAssistOpen] = useState(false)
    const [assistRequest, setAssistRequest] = useState('')
    const [assistSuggestion, setAssistSuggestion] = useState<string | null>(null)
    const [assistLoading, setAssistLoading] = useState(false)
    const [assistError, setAssistError] = useState<string | null>(null)
    const [rateLimitSeconds, setRateLimitSeconds] = useState(0)
    const [llmUnavailable, setLlmUnavailable] = useState(false)
    const rateLimitTimerRef = useRef<ReturnType<typeof setInterval>>()

    // Replace confirmation dialog
    const [showReplaceDialog, setShowReplaceDialog] = useState(false)

    // SQL compose dialog. Bundled into a single state object so open/closed and the
    // selection-vs-cursor mode cannot drift out of sync (e.g. open=false leaving stale
    // initialSql/hasSelection from the previous opening).
    const [sqlDialog, setSqlDialog] = useState<SqlDialogState>(SQL_DIALOG_CLOSED)

    // Cleanup timers
    useEffect(() => {
        return () => {
            if (syntaxSuccessTimerRef.current) clearTimeout(syntaxSuccessTimerRef.current)
            if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current)
        }
    }, [])

    // Sync externally-provided compile errors to Monaco markers so inline
    // squiggles show up when the parent owns validation.
    useEffect(() => {
        if (externalSyntaxErrors === null) {
            return
        }
        const editorInstance = editorRef.current
        const monaco = monacoRef.current
        if (!editorInstance || !monaco) {
            return
        }
        const model = editorInstance.getModel()
        if (!model) {
            return
        }
        const markers: editor.IMarkerData[] = externalSyntaxErrors
            .filter((err) => err.line > 0)
            .map((err) => ({
                severity: monaco.MarkerSeverity.Error,
                startLineNumber: err.line,
                startColumn: err.column || 1,
                endLineNumber: err.line,
                endColumn: resolveMarkerEndColumn(err, model),
                message: err.message,
            }))
        monaco.editor.setModelMarkers(model, 'syntax', markers)
    }, [externalSyntaxErrors])

    // ========================================================================
    // Editor handlers
    // ========================================================================

    function handleEditorDidMount(editorInstance: editor.IStandaloneCodeEditor, monaco: Monaco) {
        editorRef.current = editorInstance
        monacoRef.current = monaco
    }

    const handleScriptChange = useCallback((value: string | undefined) => {
        const newScript = value || ''
        onScriptChange(newScript)

        // Mark as modified after syntax check
        if (syntaxErrors && syntaxErrors.length > 0) {
            setScriptModifiedAfterCheck(true)
        }
    }, [onScriptChange, syntaxErrors])

    // ========================================================================
    // Check Syntax
    // ========================================================================

    const validateMutation = useMutation({
        mutationFn: () =>
            services.jobs.validateScript({script}) as Promise<ValidationResponse>,
        onMutate: () => {
            // Clear previous markers
            if (editorRef.current && monacoRef.current) {
                const model = editorRef.current.getModel()
                if (model) {
                    monacoRef.current.editor.setModelMarkers(model, 'syntax', [])
                }
            }
            setSyntaxErrors(undefined)
            setSyntaxSuccess(false)
            setSyntaxNetworkError(null)
            setScriptModifiedAfterCheck(false)
            if (syntaxSuccessTimerRef.current) clearTimeout(syntaxSuccessTimerRef.current)
        },
        onSuccess: (result) => {
            if (result.valid) {
                setSyntaxSuccess(true)
                toast({description: i18n.scriptEditor.syntaxOk})
                syntaxSuccessTimerRef.current = setTimeout(() => setSyntaxSuccess(false), 3000)
            } else {
                setSyntaxErrors(result.errors)
                // Set Monaco markers
                if (editorRef.current && monacoRef.current) {
                    const model = editorRef.current.getModel()
                    if (model) {
                        const markers: editor.IMarkerData[] = result.errors.map(err => ({
                            severity: monacoRef.current!.MarkerSeverity.Error,
                            startLineNumber: err.line,
                            startColumn: err.column || 1,
                            endLineNumber: err.line,
                            endColumn: resolveMarkerEndColumn(err, model),
                            message: err.message,
                        }))
                        monacoRef.current.editor.setModelMarkers(model, 'syntax', markers)
                    }
                }
            }
        },
        onError: (error) => {
            setSyntaxNetworkError(error as Error)
        },
    })

    // ========================================================================
    // Script Assist
    // ========================================================================

    async function handleAssistSuggest() {
        if (!assistRequest.trim() || assistLoading) return
        setAssistLoading(true)
        setAssistSuggestion(null)
        setAssistError(null)

        try {
            const response = await services.jobs.scriptAssist({
                request: assistRequest.trim(),
                currentCode: script || null,
                errorMessage: syntaxErrors?.map(e => `Line ${e.line}: ${e.message}`).join('\n') || null,
            }) as AssistResponse
            setAssistSuggestion(stripCodeFences(response.suggestion))
        } catch (err: unknown) {
            const apiErr = err as {
                status?: number
                message?: string
                code?: string
                responseHeaders?: Record<string, string>
                params?: Record<string, unknown>
            }
            if (apiErr.status === 429) {
                const retryAfter = resolveRetryAfterSeconds(apiErr)
                setRateLimitSeconds(retryAfter)
                if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current)
                rateLimitTimerRef.current = setInterval(() => {
                    setRateLimitSeconds(prev => {
                        if (prev <= 1) {
                            clearInterval(rateLimitTimerRef.current!)
                            return 0
                        }
                        return prev - 1
                    })
                }, 1000)
            } else if (apiErr.status === 503 || apiErr.code === ErrorCode.LLM_NOT_CONFIGURED) {
                setLlmUnavailable(true)
            } else {
                setAssistError(i18n.scriptEditor.suggestFailed as string)
            }
        } finally {
            setAssistLoading(false)
        }
    }

    function handleAssistApply() {
        if (!assistSuggestion) return
        if (script.trim()) {
            setShowReplaceDialog(true)
        } else {
            applyAssistSuggestion()
        }
    }

    function applyAssistSuggestion() {
        if (!assistSuggestion) return
        onScriptChange(assistSuggestion)
        setAssistSuggestion(null)
        setAssistRequest('')
        setShowReplaceDialog(false)
        if (syntaxErrors && syntaxErrors.length > 0) {
            setScriptModifiedAfterCheck(true)
        }
    }

    function handleInsertAtCursor() {
        if (!assistSuggestion || !editorRef.current) return
        const editorInstance = editorRef.current
        const position = editorInstance.getPosition()
        if (!position) {
            // Editor has no active cursor (never focused). Preserve the
            // suggestion so the user can retry after clicking in, and surface
            // the reason — previously we silently cleared the suggestion.
            toast({description: i18n.scriptEditor.insertAtCursorNoPosition})
            return
        }
        editorInstance.executeEdits('script-assist', [{
            range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            },
            text: assistSuggestion,
        }])
        setAssistSuggestion(null)
    }

    function handleFixWithAi() {
        if (!syntaxErrors || syntaxErrors.length === 0) return
        setAssistOpen(true)
        const errorMessages = syntaxErrors
            .map(e => `Line ${e.line}: ${e.message}`)
            .join('\n')
            .slice(0, 500)
        setAssistRequest(`${i18n.scriptEditor.fixPrefix}: ${errorMessages}`)
        setAssistSuggestion(null)
    }

    function handleOpenSqlDialog() {
        const editorInstance = editorRef.current
        if (editorInstance) {
            const selection = editorInstance.getSelection()
            const model = editorInstance.getModel()
            if (selection && model && !selection.isEmpty()) {
                setSqlDialog({open: true, initialSql: model.getValueInRange(selection), hasSelection: true})
                return
            }
        }
        setSqlDialog({open: true, initialSql: undefined, hasSelection: false})
    }

    function handleSqlOpenChange(open: boolean) {
        if (!open) {
            setSqlDialog(SQL_DIALOG_CLOSED)
        }
    }

    function handleSqlInsert(groovyCode: string) {
        const editorInstance = editorRef.current
        if (!editorInstance) return
        if (sqlDialog.open && sqlDialog.hasSelection) {
            const selection = editorInstance.getSelection()
            if (selection) {
                editorInstance.executeEdits('script-sql-insert', [{
                    range: selection,
                    text: groovyCode,
                }])
                editorInstance.focus()
                return
            }
        }
        const position = editorInstance.getPosition()
        if (!position) {
            toast({description: i18n.scriptEditor.insertAtCursorNoPosition})
            return
        }
        editorInstance.executeEdits('script-sql-insert', [{
            range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            },
            text: groovyCode,
        }])
        editorInstance.focus()
    }

    // ========================================================================
    // Derived state
    // ========================================================================

    const isScriptEmpty = !script.trim()
    const hasContent = script.trim().length > 0
    const displayedErrors = showAllErrors ? syntaxErrors : syntaxErrors?.slice(0, 5)

    // ========================================================================
    // Render
    // ========================================================================

    const insertSqlButton = (
        <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenSqlDialog}
            data-testid="btn-insert-sql"
        >
            <Database className="h-4 w-4 mr-2"/>
            {i18n.scripts.insertSql}
        </Button>
    )

    return (
        <div className={cn('flex flex-col', className)} data-testid="script-editor-core">
            {/* WHY: Insert SQL stays available even when hideSyntaxToolbar
                drops the Check Syntax button — the SQL dialog only dispatches
                a Monaco edit and never conflicts with parent-owned validators. */}
            {!hideSyntaxToolbar ? (
                <div className="flex items-center justify-end gap-2 px-4 py-1.5 shrink-0" data-testid="editor-core-toolbar">
                    {insertSqlButton}
                    {syntaxSuccess && (
                        <Badge variant={BadgeVariant.SUCCESS} className="gap-1" data-testid="badge-syntax-ok">
                            <CheckCircle className="h-3 w-3"/>
                            {i18n.scriptEditor.syntaxOk}
                        </Badge>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => validateMutation.mutate()}
                        disabled={isScriptEmpty || validateMutation.isPending}
                        data-testid="btn-check-syntax"
                    >
                        {validateMutation.isPending ? (
                            <>
                                <ButtonSpinner/>
                                {i18n.scriptEditor.validating}
                            </>
                        ) : (
                            <>
                                <CheckCircle className="h-4 w-4 mr-2"/>
                                {i18n.scriptEditor.validate}
                            </>
                        )}
                    </Button>
                </div>
            ) : (
                <div className="flex items-center justify-end gap-2 px-4 py-1.5 shrink-0" data-testid="editor-core-sql-toolbar">
                    {insertSqlButton}
                </div>
            )}

            {/* Syntax network error */}
            {!hideSyntaxToolbar && syntaxNetworkError && (
                <div className="px-4 pt-1">
                    <ErrorBanner error={syntaxNetworkError}/>
                </div>
            )}

            {/* Error summary bar */}
            {!hideSyntaxToolbar && syntaxErrors && syntaxErrors.length > 0 && (
                <div className="mx-4 mt-1 bg-destructive/10 border-destructive/20 border rounded-md px-3 py-2" data-testid="error-summary-bar">
                    {scriptModifiedAfterCheck ? (
                        <p className="text-muted-foreground text-xs">{i18n.scriptEditor.syntaxModifiedHint}</p>
                    ) : (
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                                <div className={showAllErrors && syntaxErrors.length > 5 ? 'max-h-48 overflow-y-auto' : ''}>
                                    {displayedErrors?.map((err, idx) => (
                                        <p key={idx} className="text-sm text-destructive">
                                            {i18n.scriptEditor.errorSummary.lineError({
                                                line: String(err.line),
                                                message: err.message,
                                            })}
                                        </p>
                                    ))}
                                </div>
                                {syntaxErrors.length > 5 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllErrors(!showAllErrors)}
                                        className="text-xs text-primary hover:underline mt-1"
                                        data-testid="btn-show-all-errors"
                                    >
                                        {showAllErrors
                                            ? i18n.scriptEditor.errorSummary.collapse
                                            : i18n.scriptEditor.errorSummary.showAll({count: String(syntaxErrors.length)})}
                                    </button>
                                )}
                            </div>
                            {!scriptModifiedAfterCheck && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleFixWithAi}
                                    className="shrink-0"
                                    data-testid="btn-fix-with-ai"
                                >
                                    <Sparkles className="h-3 w-3 mr-1"/>
                                    {i18n.scriptEditor.fixWithAi}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Monaco Editor */}
            <div className="h-[60vh]" data-testid="editor-container">
                <Editor
                    height="100%"
                    language="java"
                    theme={resolvedTheme === 'dark' ? 'vs-dark' : 'light'}
                    value={script}
                    onChange={handleScriptChange}
                    onMount={handleEditorDidMount}
                    options={{
                        minimap: {enabled: false},
                        lineNumbers: 'on',
                        fontSize: 14,
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        wordWrap: 'on',
                        renderWhitespace: 'selection',
                        bracketPairColorization: {enabled: true},
                        folding: true,
                    }}
                />
            </div>

            {/* Script Assist Panel */}
            <div className="border-t bg-background" data-testid="assist-panel">
                {/* Toggle button — shown when panel is closed */}
                {!assistOpen && (
                    <div className="px-4 py-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAssistOpen(true)}
                            disabled={llmUnavailable}
                            data-testid="btn-toggle-assist"
                        >
                            <Sparkles className="h-3 w-3 mr-1"/>
                            {i18n.scriptEditor.title}
                            {llmUnavailable && (
                                <Badge variant="outline" className="text-xs text-muted-foreground ml-2">
                                    {i18n.scriptEditor.llmUnavailableBadge}
                                </Badge>
                            )}
                        </Button>
                    </div>
                )}

                {/* Panel body */}
                {assistOpen && (
                    <div className="px-4 pb-4 space-y-3" data-testid="assist-panel-body">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-muted-foreground"/>
                                <span className="text-sm font-medium">{i18n.scriptEditor.title}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setAssistOpen(false)} data-testid="btn-close-assist">
                                <X className="h-3 w-3"/>
                            </Button>
                        </div>
                        {llmUnavailable ? (
                            <p className="text-sm text-muted-foreground">{i18n.scriptEditor.llmUnavailable}</p>
                        ) : (
                            <>
                                <Textarea
                                    value={assistRequest}
                                    onChange={e => setAssistRequest(e.target.value)}
                                    placeholder={i18n.scriptEditor.placeholder as string}
                                    rows={2}
                                    data-testid="input-assist-request"
                                />

                                {/* Script too large warning */}
                                {script.length > 10000 && (
                                    <p className="text-status-warning text-xs">{i18n.scriptEditor.scriptTooLarge}</p>
                                )}

                                {/* Rate limit warning */}
                                {rateLimitSeconds > 0 && (
                                    <p className="text-status-warning text-xs" data-testid="rate-limit-warning">
                                        {i18n.scriptEditor.rateLimited({seconds: String(rateLimitSeconds)})}
                                    </p>
                                )}

                                {/* Error */}
                                {assistError && (
                                    <span className="text-destructive text-xs block" data-testid="assist-error">{assistError}</span>
                                )}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAssistSuggest}
                                    disabled={!assistRequest.trim() || assistLoading || rateLimitSeconds > 0}
                                    data-testid="btn-suggest"
                                >
                                    {assistLoading ? (
                                        <>
                                            <Loader2 className="h-3 w-3 mr-1 animate-spin"/>
                                            {i18n.scriptEditor.suggesting}
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="h-3 w-3 mr-1"/>
                                            {i18n.scriptEditor.suggest}
                                        </>
                                    )}
                                </Button>

                                {/* Suggestion preview */}
                                {assistSuggestion && (
                                    <div className="space-y-2">
                                        <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap" data-testid="assist-suggestion">
                                            {assistSuggestion}
                                        </pre>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                onClick={handleAssistApply}
                                                data-testid="btn-apply-suggestion"
                                            >
                                                {i18n.scriptEditor.apply}
                                            </Button>
                                            {hasContent && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleInsertAtCursor}
                                                    data-testid="btn-insert-at-cursor"
                                                >
                                                    {i18n.scriptEditor.insertAtCursor}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Replace Confirmation Dialog */}
            <AlertDialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.scriptEditor.replaceConfirmTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.scriptEditor.replaceConfirmDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-cancel-replace">{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={applyAssistSuggestion}
                            data-testid="btn-confirm-replace"
                        >
                            {i18n.scriptEditor.replace}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* SQL Compose Dialog */}
            <ScriptSqlDialog
                open={sqlDialog.open}
                onOpenChange={handleSqlOpenChange}
                initialSql={sqlDialog.open ? sqlDialog.initialSql : undefined}
                hasSelection={sqlDialog.open && sqlDialog.hasSelection}
                onInsert={handleSqlInsert}
            />
        </div>
    )
}

// ============================================================================
// Helpers
// ============================================================================

function stripCodeFences(text: string): string {
    return text.replace(/^```(?:groovy|java)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim()
}

const DEFAULT_RETRY_AFTER_SECONDS = 60

// Reads the server's retry hint: `Retry-After` header (seconds) first, then
// `retryAfterSeconds` on the error body's params. Falls back to 60s so we
// never start a zero-length countdown on a malformed 429 response.
function resolveRetryAfterSeconds(apiErr: {
    responseHeaders?: Record<string, string>
    params?: Record<string, unknown>
}): number {
    const headerValue = apiErr.responseHeaders?.['retry-after'] ?? apiErr.responseHeaders?.['Retry-After']
    const fromHeader = parseFinitePositiveInt(headerValue)
    if (fromHeader !== null) return fromHeader
    const fromBody = parseFinitePositiveInt(apiErr.params?.retryAfterSeconds)
    if (fromBody !== null) return fromBody
    return DEFAULT_RETRY_AFTER_SECONDS
}

function parseFinitePositiveInt(raw: unknown): number | null {
    if (raw === null || raw === undefined) return null
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
    if (!Number.isFinite(n) || n <= 0) return null
    return Math.floor(n)
}

const IDENTIFIER_CHAR = /[A-Za-z0-9_$]/
const FALLBACK_TOKEN_WIDTH = 10

// Monaco columns are 1-based; endColumn is exclusive. Prefer the backend's
// endColumn when present; otherwise scan the source line forward from the
// error column until the identifier ends; otherwise fall back to a 10-column
// span (historical behaviour) or 1000 when no column is known.
function resolveMarkerEndColumn(
    err: ValidationError,
    model: {getLineContent?: (lineNumber: number) => string},
): number {
    if (err.endColumn && err.endColumn > (err.column || 0)) {
        return err.endColumn
    }
    const startCol = err.column
    if (!startCol) return 1000
    const line = model.getLineContent?.(err.line) ?? ''
    if (line) {
        let endIdx = startCol - 1
        while (endIdx < line.length && IDENTIFIER_CHAR.test(line[endIdx])) {
            endIdx++
        }
        if (endIdx > startCol - 1) {
            return endIdx + 1
        }
    }
    return startCol + FALLBACK_TOKEN_WIDTH
}
